import { useState } from "react";
import { ArrowUpRight, Menu, X } from "lucide-react";
import { Link } from "react-router";
import { useLanguage } from "../../i18n/language";
import { rewardsSiteCopy } from "./RewardsSiteCopy";

const LOGO = "/marketing/gatiq-rewards/gatiq-logo-transparent.png";

export function RewardsSiteHeader({ activePath }: { activePath: string }) {
  const { lang, setLang } = useLanguage("en");
  const [menuOpen, setMenuOpen] = useState(false);
  const copy = rewardsSiteCopy[lang];
  const links = [
    ["/", copy.nav.home],
    ["/for-karigars", copy.nav.karigars],
    ["/for-companies", copy.nav.companies],
    ["/help", copy.nav.help],
    ["/contact", copy.nav.contact],
  ] as const;

  return (
    <header className="gq-lp-header">
      <Link className="gq-lp-brand" to="/" aria-label="GatiQ Rewards — Home" onClick={() => setMenuOpen(false)}>
        <RewardsBrandLockup />
      </Link>
      <nav id="gq-lp-nav" className={menuOpen ? "gq-lp-nav is-open" : "gq-lp-nav"} aria-label={copy.nav.home}>
        {links.map(([path, label]) => (
          <Link key={path} to={path} aria-current={activePath === path ? "page" : undefined} onClick={() => setMenuOpen(false)}>
            {label}
          </Link>
        ))}
        <Link className="gq-lp-login" to="/login" onClick={() => setMenuOpen(false)}>{copy.nav.login} <ArrowUpRight size={14} /></Link>
      </nav>
      <div className="gq-lp-header-tools">
        <div className="gq-lp-language-toggle" role="group" aria-label={copy.nav.language}>
          <button type="button" aria-pressed={lang === "en"} onClick={() => setLang("en")}>English</button>
          <button type="button" aria-pressed={lang === "hi"} onClick={() => setLang("hi")}>हिन्दी</button>
        </div>
        <button
          className="gq-lp-menu-toggle"
          type="button"
          aria-expanded={menuOpen}
          aria-controls="gq-lp-nav"
          aria-label={menuOpen ? "Close menu" : "Open menu"}
          onClick={() => setMenuOpen((open) => !open)}
        >
          {menuOpen ? <X size={19} /> : <Menu size={19} />}
        </button>
      </div>
    </header>
  );
}

export function RewardsBrandLockup({ className = "" }: { className?: string }) {
  return (
    <span className={`gq-lp-brand-lockup ${className}`}>
      <span className="gq-lp-brand-mark" aria-hidden="true"><img src={LOGO} alt="" /></span>
      <span className="gq-lp-brand-copy"><b>GatiQ</b><small>Rewards</small></span>
    </span>
  );
}

export function RewardsSiteLinks({ className = "" }: { className?: string }) {
  const { lang } = useLanguage("en");
  const copy = rewardsSiteCopy[lang];

  return (
    <nav aria-label="Site links" className={className}>
      <Link to="/for-karigars">{copy.nav.karigars}</Link>
      <Link to="/for-companies">{copy.nav.companies}</Link>
      <Link to="/help">{copy.nav.help}</Link>
      <Link to="/product-film">{copy.nav.film}</Link>
      <Link to="/privacy">{copy.footer.privacy}</Link>
      <Link to="/terms">{copy.footer.terms}</Link>
      <Link to="/contact">{copy.footer.contact}</Link>
      <a href="mailto:info@gatiq.in">{copy.footer.email}</a>
    </nav>
  );
}

export function RewardsSiteFooter({ showLinks = true }: { showLinks?: boolean }) {
  return (
    <footer className="gq-lp-footer gq-lp-site-footer">
      <span>© {new Date().getFullYear()} GatiQ Platforms LLP. All rights reserved.</span>
      {showLinks ? <RewardsSiteLinks /> : null}
    </footer>
  );
}
